import { motion } from "framer-motion";
import { ArrowRight, BarChart3, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { candidates, states } from "../services/api";

const ExploreCandidates = () => {
  const [allCandidates, setAllCandidates] = useState([]);
  const [availableStates, setAvailableStates] = useState([]);
  const [filters, setFilters] = useState({
    state: "",
    position: "",
    party: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [candidateResponse, stateResponse] = await Promise.all([
          candidates.getAll(),
          states.getAll(),
        ]);
        setAllCandidates(Array.isArray(candidateResponse.data) ? candidateResponse.data : []);
        setAvailableStates(Array.isArray(stateResponse.data) ? stateResponse.data : []);
      } catch (error) {
        console.error("Failed to load candidates:", error);
        toast.error("Failed to load representatives");
      }
    };

    fetchData();
  }, []);

  const partyOptions = useMemo(
    () =>
      [...new Set(allCandidates.map((candidate) => candidate.party).filter(Boolean))].sort(),
    [allCandidates],
  );

  const filteredCandidates = useMemo(
    () =>
      allCandidates.filter((candidate) => {
        return (
          (!filters.state || candidate.state === filters.state) &&
          (!filters.position || candidate.position === filters.position) &&
          (!filters.party || candidate.party === filters.party)
        );
      }),
    [allCandidates, filters],
  );

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-green-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <h1 className="text-4xl font-bold text-orange-600">Explore Leaders of Bharat</h1>
          <p className="text-gray-600 mt-2">
            Browse MPs, MLAs, and CMs sourced from the live state dataset.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <select
            name="state"
            value={filters.state}
            onChange={handleFilterChange}
            className="p-3 rounded-lg border"
          >
            <option value="">All States</option>
            {availableStates.map((state) => (
              <option key={state.state_name} value={state.state_name}>
                {state.state_name}
              </option>
            ))}
          </select>

          <select
            name="position"
            value={filters.position}
            onChange={handleFilterChange}
            className="p-3 rounded-lg border"
          >
            <option value="">All Positions</option>
            <option value="MLA">MLA</option>
            <option value="MP">MP</option>
            <option value="CM">CM</option>
          </select>

          <select
            name="party"
            value={filters.party}
            onChange={handleFilterChange}
            className="p-3 rounded-lg border"
          >
            <option value="">All Parties</option>
            {partyOptions.map((party) => (
              <option key={party} value={party}>
                {party}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {filteredCandidates.map((candidate, index) => (
            <motion.div
              key={`${candidate.name}-${candidate.position}-${index}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-orange-500"
            >
              <div className="flex justify-between items-center mb-4 gap-4">
                <h3 className="text-xl font-semibold text-gray-800">{candidate.name}</h3>
                <span className="text-sm bg-green-100 text-green-700 px-2 py-1 rounded-full">
                  {candidate.position}
                </span>
              </div>
              <p className="text-sm text-gray-500 mb-1">Party: {candidate.party || "Unknown"}</p>
              <p className="text-sm text-gray-500 mb-1">State: {candidate.state || "Unknown"}</p>
              <p className="text-sm text-gray-500">
                Constituency: {candidate.constituency || "Unknown"}
              </p>

              <div className="mt-4 space-y-2">
                <Link
                  to={`/candidate/${encodeURIComponent(candidate.name)}`}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                >
                  <Users size={16} /> View Profile
                </Link>
                <Link
                  to={`/raise-issue?state=${encodeURIComponent(
                    candidate.state || "",
                  )}&constituency=${encodeURIComponent(
                    candidate.constituency || "",
                  )}&to=${candidate.position.toLowerCase()}&name=${encodeURIComponent(
                    candidate.name,
                  )}`}
                  className="flex items-center gap-2 text-sm text-green-600 hover:underline"
                >
                  <BarChart3 size={16} /> Raise Issue
                </Link>
                <Link
                  to={`/state/${encodeURIComponent(candidate.state || "")}`}
                  className="flex items-center gap-2 text-sm text-orange-600 hover:underline"
                >
                  <ArrowRight size={16} /> View State Page
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExploreCandidates;
